import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
} from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { motion, useReducedMotion } from "framer-motion"
import { ArrowRight, CalendarDays, Plus } from "lucide-react"
import { api } from "../../../../convex/_generated/api"

export const Route = createFileRoute("/_authed/host/events")({
  component: EventsRoute,
})

function EventsRoute() {
  const location = useLocation()
  const isIndex =
    location.pathname === "/host/events" ||
    location.pathname === "/host/events/"

  return isIndex ? <EventsList /> : <Outlet />
}

function EventsList() {
  const events = useQuery(api.events.listMine)
  const reduce = useReducedMotion()

  const stagger = reduce ? 0 : 0.04
  const enterDuration = reduce ? 0 : 0.18

  return (
    <div className="space-y-4">
      <div className="console-panel rounded-[1.35rem] p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
            <p className="console-kicker">Event planning</p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight">Events</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-[color:var(--muted-foreground)]">
              Schedule Lyrically Challenged nights and jump into host controls.
            </p>
        </div>
        <Link
          to="/host/events/new"
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground shadow-[0_14px_32px_-22px_rgba(67,65,201,0.9)] transition-all duration-150 hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 active:scale-[0.98]"
        >
            <Plus className="h-4 w-4" aria-hidden />
            New event
        </Link>
        </div>
      </div>

      {events === undefined ? (
        <ListSkeleton rows={4} />
      ) : events.length === 0 ? (
        <EmptyEvents />
      ) : (
        <motion.ul
          className="console-panel divide-y divide-[color:var(--border)] overflow-hidden rounded-[1.35rem]"
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: stagger } },
          }}
        >
          {events.map((event) => (
            <motion.li
              key={event._id}
              variants={{
                hidden: { opacity: 0, y: reduce ? 0 : 4 },
                show: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: enterDuration, ease: "easeOut" },
                },
              }}
            >
              <Link
                to="/host/events/$eventId"
                params={{ eventId: event._id }}
                className="group flex items-center justify-between gap-4 px-4 py-3.5 transition-colors duration-150 hover:bg-[color:var(--surface-soft)] focus-visible:bg-[color:var(--surface-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40 active:bg-[color:var(--surface-hover)]"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <DateTile startsAt={event.startsAt} />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold">{event.name}</p>
                    <p className="truncate text-xs text-[color:var(--muted-foreground)]">
                      {new Date(event.startsAt).toLocaleString(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <StatusBadge status={event.status} />
                  <ArrowRight
                    className="hidden h-4 w-4 text-[color:var(--muted-foreground)] transition-transform group-hover:translate-x-0.5 sm:block"
                    aria-hidden
                  />
                </div>
              </Link>
            </motion.li>
          ))}
        </motion.ul>
      )}
    </div>
  )
}

function ListSkeleton({ rows }: { rows: number }) {
  return (
    <ul className="console-panel divide-y divide-[color:var(--border)] rounded-[1.35rem]">
      {Array.from({ length: rows }).map((_, i) => (
        <li
          key={i}
          className="flex items-center justify-between gap-4 px-4 py-3"
        >
          <div className="flex-1 space-y-2">
            <div className="h-3 w-1/3 animate-pulse rounded bg-[color:var(--muted)]" />
            <div className="h-2.5 w-1/2 animate-pulse rounded bg-[color:var(--muted)]" />
          </div>
          <div className="h-4 w-16 animate-pulse rounded-full bg-[color:var(--muted)]" />
        </li>
      ))}
    </ul>
  )
}

function EmptyEvents() {
  const reduce = useReducedMotion()
  return (
    <motion.div
      initial={{ opacity: 0, y: reduce ? 0 : 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: "easeOut" }}
      className="console-panel rounded-[1.35rem] border-dashed p-9 text-center"
    >
      <div
        className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--muted-foreground)]"
        aria-hidden
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3v4M8 3v4M3 11h18" />
          <path d="M8 15h2M14 15h2M8 19h2" />
        </svg>
      </div>
      <p className="mt-4 text-sm font-bold">No events yet</p>
      <p className="mx-auto mt-1 max-w-xs text-xs leading-5 text-[color:var(--muted-foreground)]">
        Create the event and first Lyrically Challenged game in one pass.
      </p>
      <Link
        to="/host/events/new"
        className="mt-5 inline-flex min-h-10 items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground shadow-[0_14px_32px_-22px_rgba(67,65,201,0.9)] transition-all duration-150 hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 active:scale-[0.98]"
      >
        <Plus className="h-4 w-4" aria-hidden />
        New event
      </Link>
    </motion.div>
  )
}

function DateTile({ startsAt }: { startsAt: number }) {
  const date = new Date(startsAt)
  return (
    <span className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface-soft)] text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.82)]">
      <CalendarDays className="mb-0.5 h-3.5 w-3.5 text-[color:var(--primary)]" aria-hidden />
      <span className="text-[10px] font-bold uppercase leading-none text-[color:var(--muted-foreground)]">
        {date.toLocaleDateString(undefined, { month: "short" })}
      </span>
      <span className="text-sm font-bold leading-none tabular-nums">
        {date.getDate()}
      </span>
    </span>
  )
}

function StatusBadge({ status }: { status: string }) {
  const tone: Record<string, string> = {
    scheduled:
      "border-[color:var(--border)] bg-[color:var(--background)] text-[color:var(--muted-foreground)]",
    ready: "border-amber-300 bg-amber-50 text-amber-800",
    active: "border-emerald-300 bg-emerald-50 text-emerald-800",
    completed:
      "border-[color:var(--border)] bg-[color:var(--muted)] text-[color:var(--muted-foreground)]",
    cancelled: "border-red-200 bg-red-50 text-red-700",
  }
  const cls =
    tone[status] ??
    "border-[color:var(--border)] bg-[color:var(--background)] text-[color:var(--muted-foreground)]"
  return (
    <span
      className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide ${cls}`}
    >
      {status === "cancelled" ? "canceled" : status}
    </span>
  )
}
